import React from 'react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';
import { useMemoOne } from 'use-memo-one';

import { routeNode } from 'core/routing';

import DocumentHead from 'legacyComponents/documentHead';

import { useRouterStore, useAppManager } from 'apps/hooks';
import { FourOhFour } from 'apps/fourOhFour/index';
import Header from 'apps/components/header/header';
import { clientStore } from 'apps/client/app';
import { getCanceledTextForOutsideCollaborators, stripTags } from 'apps/utils';

import { ContributionPage } from 'apps/repository/contributionPage/index';
import { RepoAppManager } from 'apps/repository/appManager';
import { IssuePage } from 'apps/issueTracker/views/issuePage';
import { imageSrc } from 'apps/file/utils';

import { conf as globalConf } from 'apps/config.yaml';
import { ProjectName } from 'apps/project/views/projectName';
import StatusMessage from 'apps/project/views/statusMessage';
import ImportLoading from 'apps/project/views/importLoading';
import { ProjectAppManager } from 'apps/project/appManager';
import { PaymentSpaceApp } from 'apps/payment/app';
import { ProjectHome } from 'apps/project/tabs/home';
import { frameStore } from 'apps/components/Frame/appManager';

import {
  ProfileFilesRouter,
  ProfileFilesVersionRouter,
  InitiativeFilesRouter,
  InitiativeFilesVersionRouter,
} from 'apps/project/tabs/files/router';

import { FilePage } from 'apps/project/tabs/file/filePage';
import { ConflictsPage } from 'apps/project/tabs/conflicts/index';
import { IssuesPage } from 'apps/project/tabs/issues';
import { ProjectTabsBar } from 'apps/project/views/tabsBar';
import { PlanMonitor } from 'apps/initiative/views/PlanMonitor';

import {
  ProfileProjectSettingsRouter,
  InitiativeProjectSettingsRouter,
} from 'apps/project/settings/router';
import { StatButtons } from 'apps/project/views/statButtons';
import { CancelledPlanForTeam } from 'apps/initiative/views/CancelledPlanForTeam';
import { SubscriptionAppManager } from 'apps/payment/subscription/appManager';

import { GltfLoading } from 'libs/rendering/gltf/components/loading';

import { ProjectNameWrapper } from './styles';

export const generateFileViewsMap = (spaceType, version = false) => {
  // Instead of being looked up in the views Map, "FilePage" is currently
  // being "short-circuited" to in the ProjectSpace router based on
  // "atFilePage", in order to render the full screen file page without other
  // project page components above it (Header, ProjectName, ProjectTabsBar etc).
  // Have kept in in the ViewsMap as a reminder, or in case we refactor these
  // views in the future.
  if (version) {
    return {
      'version.files':
        spaceType === 'initiative'
          ? InitiativeFilesVersionRouter
          : ProfileFilesVersionRouter,
      'version.file': FilePage, // * see comment above.
      version: ProjectHome,
    };
  }
  return {
    files:
      spaceType === 'initiative' ? InitiativeFilesRouter : ProfileFilesRouter,
    file: FilePage, // * see comment above.
  };
};

const commonProjectViews = {
  '': ProjectHome,
  issues: IssuesPage,
  issue: IssuePage,
  contribution: ContributionPage,
  conflicts: ConflictsPage,
};

const profileViewsMap = {
  ...commonProjectViews,
  ...generateFileViewsMap('profile'),
  ...generateFileViewsMap('profile', true),
  settings: ProfileProjectSettingsRouter,
};

const initiativeViewsMap = {
  ...commonProjectViews,
  ...generateFileViewsMap('initiative'),
  ...generateFileViewsMap('initiative', true),
  settings: InitiativeProjectSettingsRouter,
};

function isFileRoute(route) {
  if (
    route.name.endsWith('project.file') ||
    route.name.endsWith('project.version.file')
  ) {
    return true;
  }
  return false;
}

const ProjectSpace = observer(props => {
  const { route, params } = useRouterStore();
  const atFilePage = isFileRoute(route);

  const projectManager = useAppManager(
    ProjectAppManager,
    null,
    {
      ...props,
      params,
    },
    !atFilePage,
    [route.params.slug, route.params.initiative, route.params.profile],
  );

  const repoManager = useAppManager(
    RepoAppManager,
    projectManager,
    {
      ...props,
      params,
    },
    !!projectManager,
    [projectManager],
  );
  const spaceSlug = route.params.profile || route.params.initiative;
  const subscriptionManager = useAppManager(
    SubscriptionAppManager,
    projectManager,
    {
      ...props,
      params,
      spaceSlug,
    },
    !!projectManager,
  );
  // TODO: the following is actually dead code? ProjectTabsBar does not really use it
  const payment = useMemoOne(() => {
    if (!subscriptionManager) return null;
    const app = new PaymentSpaceApp(subscriptionManager, {
      ...props,
      params,
      spaceSlug,
    });
    return app;
  }, [subscriptionManager]);

  const project = projectManager?.project;

  const { canDeleteIn } = clientStore.currentSpaceData || {};

  const isAdmin = clientStore.currentSpaceData && canDeleteIn;

  const isRestrictedSpace =
    (frameStore.project?.isRestricted || project?.isRestricted) &&
    clientStore.currentSpaceData;

  // render the full screen file page without other project components above it.
  if (atFilePage) {
    // file page also should check outside collaborator access when plan was canceled
    if (isRestrictedSpace) {
      return (
        <CancelledPlanForTeam
          isAdminAndPlanWasCanceled={isAdmin}
          cancellationNotice={
            !isAdmin && getCanceledTextForOutsideCollaborators(spaceSlug)
          }
        />
      );
    }

    return <FilePage params={params} />;
  }

  const profileName = params.get('profile');
  const initiativeName = params.get('initiative');

  if (projectManager.loading) {
    return <GltfLoading visible noText />;
  }

  if (projectManager?.notFound) {
    return <FourOhFour type='Project' />;
  }

  // Display a 404 page if we don't have permissions to see settings.
  // Bad solution, actual solution would be to check whether the View that is going to be rendered
  // by the inner router is a 404 component, in which case we don't render everything else around the <View />
  // just it. I don't know that we have a good way to detect 404 in the nested routers.
  if (route.name.includes('settings') && project && !project.canUpdate) {
    return <FourOhFour type='Project' />;
  }

  if (!project) return <GltfLoading visible noText />; // Note: This must short circuits the project layout loader!
  const formStore = project?.editing ? project?.editFormStore : null;

  const View = props.componentFromRoute();
  return (
    <>
      {project !== null && (
        <DocumentHead
          title={`${project.name} by ${profileName || initiativeName}`}
          description={stripTags(project.description)}
          imagePath={imageSrc(
            project.image,
            '',
            globalConf.imgSizes.project.large,
          )}
        />
      )}
      <Header />
      <ProjectNameWrapper>
        <ProjectName app={project} fieldName='name' formStore={formStore} />
        {project && <StatButtons projectManager={projectManager} />}
      </ProjectNameWrapper>
      {project?.isImporting ? (
        <>
          <StatusMessage
            status={!projectManager.retryToken && project.importStatus.status}
            tryAgain={projectManager.ShowAuthModal}
            cancelImport={projectManager.CancelImportAction}
          />
          <ImportLoading
            status={!projectManager.retryToken && project.importStatus.status}
            generalProgress={project.importStatus.generalProgress}
            stepProgress={project.importStatus.generalProgress}
          />
        </>
      ) : (
        <>
          <ProjectTabsBar
            route={route}
            project={project}
            payment={payment}
            repoManager={repoManager}
            subscriptionManager={subscriptionManager}
          />

          {isRestrictedSpace ? (
            <CancelledPlanForTeam
              isAdminAndPlanWasCanceled={isAdmin}
              cancellationNotice={
                !isAdmin && getCanceledTextForOutsideCollaborators(spaceSlug)
              }
            />
          ) : (
            <PlanMonitor>
              <View
                params={params}
                project={project}
                projectManager={projectManager}
                repoManager={repoManager}
              />
              <div id='post-wrapper-section' />
            </PlanMonitor>
          )}
        </>
      )}
    </>
  );
});

export const ProfileProjectRouter = routeNode(
  'profile.project',
  profileViewsMap,
  FourOhFour,
)(ProjectSpace);
export const InitiativeProjectRouter = routeNode(
  'initiative.project',
  initiativeViewsMap,
  FourOhFour,
)(ProjectSpace);

ProjectSpace.propTypes = {
  componentFromRoute: PropTypes.func.isRequired,
};
